class PredictiveSearch extends Theme.Component {
  connectedCallback() {
    super.connectedCallback();

    this.header = this.closest("component-header");
    this.mode = this.dataset.openOn || "dispatch";
    this.sectionId = this.dataset.sectionId || "predictive-search";

    if (this.mode === "dispatch") {
      this.trigger = document.querySelector('[data-dispatch="searchlink:click"]');
    }

    this.cache = {};
    this.requestId = 0;
    this.controller = null;
    this.term = "";

    if (this.mode === "dispatch") {
      this.addDirectEvent("searchlink:click", document, () => this.toggle());
    } else {
      this.addEvent("focusin", this.elements.input, () => this.show());
    }

    this.addDirectEvent("megamenu:open", document, () => this.close());
    this.addDirectEvent("keydown", document, (event) => this.onKeydown(event));
    this.addDirectEvent("click", document, (event) => this.onDocumentClick(event));

    this.addEvent("input", this.elements.input, Theme.Helpers.debounce(() => this.onInput(), 300));
    this.addEvent("click", this.elements.clear, () => this.clear());

    if (this.elements.close) {
      this.addEvent("click", this.elements.close, () => this.close());
    }

    if (this.mode === "dispatch" && Theme.URLParams.has("search")) {
      this.show();
    }
  }

  disconnectedCallback() {
    this.cancel();

    clearTimeout(this.statusTimer);

    super.disconnectedCallback();
  }

  get open() {
    return this.classList.contains("is-open");
  }

  get panel() {
    return this.querySelector(".predictive-search-panel");
  }

  setPanelHidden(hidden) {
    if (!this.panel) {
      return;
    }

    this.panel.setAttribute("aria-hidden", hidden ? "true" : "false");
    this.panel.toggleAttribute("inert", hidden);
  }

  toggle() {
    if (this.open) {
      this.close();
    } else {
      this.show();
    }
  }

  show() {
    if (this.open) {
      return;
    }

    document.dispatchEvent(new CustomEvent("predictivesearch:open"));

    this.classList.add("is-open");
    this.setPanelHidden(false);

    if (this.trigger) {
      this.trigger.setAttribute("aria-expanded", "true");
    }

    if (this.mode === "dispatch") {
      Theme.URLParams.set("search", "open");
    }

    if (this.header) {
      this.header.classList.add("is-mega-open");
    }

    this.elements.input.focus();
  }

  close() {
    if (!this.open) {
      return;
    }

    this.cancel();
    this.setLoading(false);

    if (this.mode === "focus") {
      this.elements.input.blur();
    }

    this.classList.remove("is-open");
    this.setPanelHidden(true);

    if (this.trigger) {
      this.trigger.setAttribute("aria-expanded", "false");
    }

    if (this.mode === "dispatch") {
      Theme.URLParams.delete("search");
    }

    if (this.header) {
      this.header.classList.remove("is-mega-open");
    }
  }

  onInput() {
    const term = this.elements.input.value.trim();

    this.elements.clear.hidden = term.length === 0;

    if (term.length === 0) {
      this.clear();
      return;
    }

    if (!this.term || !term.startsWith(this.term)) {
      this.elements.results.innerHTML = "";
    }

    this.term = term;

    this.setLoading(true);
    this.announce(this.dataset.loadingText);
    this.search(term);
  }

  clear() {
    this.cancel();
    this.setLoading(false);

    this.term = "";

    this.elements.input.value = "";
    this.elements.clear.hidden = true;
    this.elements.results.innerHTML = "";

    this.showDefault(true);
    this.elements.input.focus();
  }

  search(term) {
    const key = term.toLowerCase();

    this.cancel();

    if (this.cache[key]) {
      this.render(this.cache[key]);
      return;
    }

    const requestId = ++this.requestId;

    this.controller = new AbortController();

    const params = new URLSearchParams({
      q: term,
      "resources[type]": "product,collection,page",
      "resources[limit]": "10",
      "resources[options][unavailable_products]": "last",
      section_id: this.sectionId
    });

    fetch(`${this.dataset.predictiveSearchUrl}?${params}`, { signal: this.controller.signal })
      .then((response) => {
        if (!response.ok) {
          throw new Error(response.status);
        }

        return response.text();
      })
      .then((html) => {
        if (requestId !== this.requestId || term !== this.term) {
          return;
        }

        const parsed = new DOMParser().parseFromString(html, "text/html");
        const section = parsed.querySelector(`#shopify-section-${this.sectionId}`);

        if (!section) {
          this.setLoading(false);
          return;
        }

        this.cache[key] = section.innerHTML;
        this.render(section.innerHTML);
      })
      .catch((error) => {
        if (error.name === "AbortError") {
          return;
        }

        this.setLoading(false);
      });
  }

  cancel() {
    if (this.controller) {
      this.controller.abort();
      this.controller = null;
    }

    this.requestId += 1;
  }

  render(html) {
    this.elements.results.innerHTML = html;

    this.showDefault(false);
    this.setLoading(false);

    const count = this.results().length;

    this.announce(count === 0 ? `No results for ${this.term}` : `${count} results for ${this.term}`);
  }

  results() {
    return Theme.querySelectorAll("a", this.elements.results);
  }

  showDefault(visible) {
    this.elements.default.hidden = !visible;
  }

  setLoading(loading) {
    this.elements.body.classList.toggle("state--loading", loading);
  }

  announce(text) {
    clearTimeout(this.statusTimer);

    this.elements.status.textContent = text;

    this.statusTimer = setTimeout(() => {
      this.elements.status.textContent = "";
    }, 1000);
  }

  onDocumentClick(event) {
    if (!this.open || this.contains(event.target)) {
      return;
    }

    if (event.target.closest('[data-dispatch="searchlink:click"]')) {
      return;
    }

    this.close();
  }

  onKeydown(event) {
    if (!this.open) {
      return;
    }

    if (event.key === "Escape") {
      this.close();
      this.trigger?.focus();
      return;
    }

    if (event.key !== "ArrowDown" && event.key !== "ArrowUp") {
      return;
    }

    const links = Theme.querySelectorAll("a", this.elements.body);

    if (links.length === 0) {
      return;
    }

    event.preventDefault();

    const current = links.indexOf(document.activeElement);

    let next = 0;

    if (event.key === "ArrowDown") {
      next = current < 0 ? 0 : (current + 1) % links.length;
    } else {
      next = current < 0 ? links.length - 1 : (current - 1 + links.length) % links.length;
    }

    links[next].focus();
  }
}

customElements.define("predictive-search", PredictiveSearch);
